// RoleRow — one role in the list (from GET /roles). Tone/icon from isOwner,
// lock when isDefault (system); chips are the ADMIN module titles; counts come
// straight off the list item.
//
// The whole row stays clickable regardless of permissions: it opens the editor,
// which doubles as the read-only viewer, so navigating there is a read path. The
// trailing Edit button is the write affordance, and its condition mirrors the
// editor's own `modifiable` test — system AND owner roles are immutable, so both
// show the chevron rather than a button that opens a page you cannot edit.

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Icon } from '@/components/common';
import useModuleAccess from '@/hooks/useModuleAccess';
import type { RoleListItem } from '@/api/endpoints/rolesApi';

interface RoleRowProps {
  role: RoleListItem;
}

const CHIP_LIMIT = 5;

const RoleRow = ({ role }: RoleRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canEdit } = useModuleAccess('roles');
  const webModules = role.module.filter((m) => m.type === 'admin');
  const shown = webModules.slice(0, CHIP_LIMIT);
  const overflow = webModules.length - shown.length;
  const tone = role.isOwner ? 'violet' : 'brand';

  const open = () => navigate(`/roles/${role.id}`);

  return (
    <div
      className='roles-row'
      role='button'
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <span className={`roles-row__tile roles-row__tile--${tone}`}>
        <Icon name={role.isOwner ? 'crown' : 'shield-check'} size={20} />
      </span>

      <div className='roles-row__head'>
        <span className='roles-row__name'>
          {role.name}
          {role.isDefault && <Icon name='lock' size={14} className='roles-row__lock' />}
        </span>
        <span className='roles-row__desc'>{role.description}</span>
      </div>

      <div className='roles-row__chips'>
        {shown.map((m) => (
          <Chip key={m.title} variant='neutral'>
            {m.title}
          </Chip>
        ))}
        {overflow > 0 && <Chip variant='neutral'>+{overflow}</Chip>}
      </div>

      <div className='roles-row__stats'>
        <span className='roles-row__stat' title={t('roles_web')}>
          <Icon name='monitor' size={15} strokeWidth={2} />
          {role.adminPermission}
        </span>
        <span className='roles-row__stat' title={t('roles_mobile')}>
          <Icon name='smartphone' size={15} strokeWidth={2} />
          {role.userPermission}
        </span>
        <span className='roles-row__stat' title={t('roles_users')}>
          <Icon name='users' size={15} strokeWidth={2} />
          {role.totalUsers}
        </span>
      </div>

      {!canEdit || role.isDefault || role.isOwner ? (
        <Icon name='chevron-right' size={18} className='roles-row__go' />
      ) : (
        <Button
          variant='secondary'
          size='sm'
          iconStart='square-pen'
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          {t('roles_edit')}
        </Button>
      )}
    </div>
  );
};

export default RoleRow;
